/**
 * A utility type that takes two types, `T` and `U`, and creates a new type
 * by overwriting the properties of `T` with the properties from `U`.
 * @template T - The base type.
 * @template U - The type containing the properties to overwrite.
 */
export type Overwrite<T, U> = Omit<T, keyof U> & U;

export type JsonScalar = string | number | boolean | null | undefined;

export interface JsonObject {
  [key: string]: JsonValue;
}

export type JsonValue = JsonScalar | JsonValue[] | JsonObject;

/** Deeply checks if a value is a JSON value: scalar, array, or object. */
export function isJsonValue(value: unknown): value is JsonValue {
  return isJsonScalar(value) || isJsonArray(value) || isJsonObject(value);
}

/** Checks if a value is a JSON scalar: string, number, boolean, null, or undefined. */
export function isJsonScalar(value: unknown): value is JsonScalar {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

/** Deeply checks if a value is a JSON array all the way down. */
export function isJsonArray(value: unknown): value is JsonValue[] {
  return Array.isArray(value) && value.every(isJsonValue);
}

/** Deeply checks if a value is a JSON object all the way down. */
export function isJsonObject(value: unknown): value is { [key: string]: JsonValue } {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.values(value).every(isJsonValue) &&
    Object.keys(value).every((x) => typeof x === 'string')
  );
}

export function isFn<R>(arity: 0, val: unknown): val is Fn0<R>;

export function isFn<A1, R>(arity: 1, val: unknown): val is Fn1<A1, R>;

export function isFn<A1, A2, R>(arity: 2, val: unknown): val is Fn2<A1, A2, R>;

export function isFn<A1, A2, A3, R>(arity: 3, val: unknown): val is Fn3<A1, A2, A3, R>;

export function isFn<A1, A2, A3, A4, R>(arity: 4, val: unknown): val is Fn4<A1, A2, A3, A4, R>;

export function isFn<A1, A2, A3, A4, A5, R>(arity: 5, val: unknown): val is Fn5<A1, A2, A3, A4, A5, R>;

export function isFn<A1, A2, A3, A4, A5, A6, R>(arity: 6, val: unknown): val is Fn6<A1, A2, A3, A4, A5, A6, R>;

export function isFn<A1, A2, A3, A4, A5, A6, A7, R>(arity: 7, val: unknown): val is Fn7<A1, A2, A3, A4, A5, A6, A7, R>;

export function isFn<A1, A2, A3, A4, A5, A6, A7, A8, R>(
  arity: 8,
  val: unknown,
): val is Fn8<A1, A2, A3, A4, A5, A6, A7, A8, R>;

export function isFn<A1, A2, A3, A4, A5, A6, A7, A8, A9, R>(
  arity: 9,
  val: unknown,
): val is Fn9<A1, A2, A3, A4, A5, A6, A7, A8, A9, R>;

export function isFn<A1, A2, A3, A4, A5, A6, A7, A8, A9, A10, R>(
  arity: 10,
  val: unknown,
): val is Fn10<A1, A2, A3, A4, A5, A6, A7, A8, A9, A10, R>;

export function isFn<R, Args extends unknown[]>(arity: number | undefined, val: unknown): val is Fn<Args, R>;

export function isFn<R, Args extends unknown[]>(val: unknown): val is Fn<Args, R>;

export function isFn(arg1: unknown, arg2?: unknown): arg2 is Function {
  let arity: number | undefined;
  let candidate: unknown;
  if (typeof arg1 === 'number' || arg1 === undefined) {
    arity = arg1;
    candidate = arg2;
  } else if (typeof arg1 === 'function') {
    arity = undefined;
    candidate = arg1;
  } else {
    return false;
  }
  if (typeof candidate !== 'function') return false;
  if (arity !== undefined) return candidate.length === arity;
  return true;
}

export type Predicate<T> = (value: unknown) => value is T;

export function predicate<T>(checkFn: (value: unknown) => boolean): Predicate<T> {
  return (value: unknown): value is T => checkFn(value);
}

export type FnArity = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export type Fn<Args extends unknown[], R> = (...args: Args) => R;

export type Fn0<R> = () => R;

export type Fn1<A1, R> = Fn<[A1], R>;

export type Fn2<A1, A2, R> = Fn<[A1, A2], R>;

export type Fn3<A1, A2, A3, R> = Fn<[A1, A2, A3], R>;

export type Fn4<A1, A2, A3, A4, R> = Fn<[A1, A2, A3, A4], R>;

export type Fn5<A1, A2, A3, A4, A5, R> = Fn<[A1, A2, A3, A4, A5], R>;

export type Fn6<A1, A2, A3, A4, A5, A6, R> = Fn<[A1, A2, A3, A4, A5, A6], R>;

export type Fn7<A1, A2, A3, A4, A5, A6, A7, R> = Fn<[A1, A2, A3, A4, A5, A6, A7], R>;

export type Fn8<A1, A2, A3, A4, A5, A6, A7, A8, R> = Fn<[A1, A2, A3, A4, A5, A6, A7, A8], R>;

export type Fn9<A1, A2, A3, A4, A5, A6, A7, A8, A9, R> = Fn<[A1, A2, A3, A4, A5, A6, A7, A8, A9], R>;

export type Fn10<A1, A2, A3, A4, A5, A6, A7, A8, A9, A10, R> = Fn<[A1, A2, A3, A4, A5, A6, A7, A8, A9, A10], R>;

export type Awaitable<T> = T | Promise<T>;

export type Metadata = Record<string, unknown>;

export function isPromiseLike<T>(value: unknown): value is PromiseLike<T> {
  return typeof value === 'object' && value !== null && 'then' in value;
}

export type Meta = Record<string, unknown>;

/**
 * Configuration for tool retry behavior.
 * Used in tool metadata to control automatic retries on failure.
 */
export interface RetryConfig {
  /** Enable retry on failure. Default: false */
  retryable?: boolean;
  /** Maximum retry attempts (in addition to initial attempt). Default: 1 */
  maxRetries?: number;
  /** Delay between retries in milliseconds. Default: 1000 */
  retryDelayMs?: number;
}

export function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

export function isRecord<K extends PropertyKey, V>(value: unknown): value is Record<K, V> {
  if (typeof value !== 'object' || value === null) return false;
  if (Array.isArray(value)) return false;
  return true;
}

export function isURL(value: unknown): value is URL {
  return value instanceof URL;
}

export function isBigInt(value: unknown): value is bigint {
  return typeof value === 'bigint';
}

export type Disposer = () => void;

export type RecordToTuple<T> = Array<{ [K in keyof T]: [K, T[K]] }[keyof T]>;
